import {
  Button,
  createStyles,
  LinearProgress,
  makeStyles,
  Paper,
  Typography,
} from "@material-ui/core"
import CloudUploadIcon from "@material-ui/icons/CloudUpload"
import MicIcon from "@material-ui/icons/Mic"
import StopIcon from "@material-ui/icons/Stop"
import {
  ChangeEvent,
  FC,
  MutableRefObject,
  MouseEvent as ReactMouseEvent,
  useEffect,
  useRef,
} from "react"
import { colors } from "../../../utils/theme"

interface TranscriptCapturePanelProps {
  isRecording: boolean
  isPaused: boolean
  isUploading: boolean
  isProcessing: boolean
  isFileUploading: boolean
  onRequestStartRecording?: (event: ReactMouseEvent<HTMLElement>) => void
  onPauseRecording?: () => void
  onResumeRecording?: () => void
  onStopRecording?: () => void
  onFileSelected: (file: File) => void
  audioMeterFillRef?: MutableRefObject<HTMLDivElement | null>
  registerElapsedTimeElement?: (element: HTMLElement) => () => void
}

/**
 * Renders transcript recording controls, upload controls, and capture status.
 * File validation and upload behavior remain owned by the parent component.
 */
export default function TranscriptCapturePanel({
  isRecording,
  isPaused,
  isUploading,
  isProcessing,
  isFileUploading,
  onRequestStartRecording,
  onPauseRecording,
  onResumeRecording,
  onStopRecording,
  onFileSelected,
  audioMeterFillRef,
  registerElapsedTimeElement,
}: TranscriptCapturePanelProps) {
  const classes = useStyles()
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const isBusy = isUploading || isProcessing || isFileUploading
  const isCaptureActive = isRecording || isPaused

  function handleFileSelected(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0]
    event.target.value = ""

    if (file) {
      onFileSelected(file)
    }
  }

  return (
    <Paper className={classes.capturePanel} elevation={0}>
      <Typography variant="body1" className={classes.captureTitle}>
        Transcript Capture
      </Typography>
      <Typography variant="body2" className={classes.captureSubtitle}>
        Use the record pill in the header to start a live recording, or upload a
        completed transcript file here.
      </Typography>

      <div className={classes.actionsRow}>
        {!isCaptureActive ? (
          <Button
            variant="contained"
            color="primary"
            startIcon={<MicIcon />}
            onClick={onRequestStartRecording}
            disabled={isBusy}
          >
            Open Recorder
          </Button>
        ) : (
          <>
            <Button
              variant="outlined"
              color="primary"
              onClick={isPaused ? onResumeRecording : onPauseRecording}
              disabled={isPaused ? !onResumeRecording : !onPauseRecording}
            >
              {isPaused ? "Resume" : "Pause"}
            </Button>
            <Button
              variant="contained"
              className={classes.stopButton}
              startIcon={<StopIcon />}
              onClick={onStopRecording}
              disabled={isUploading}
            >
              Stop Recording
            </Button>
          </>
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
          className={classes.hiddenInput}
          onChange={handleFileSelected}
        />
        <Button
          variant="outlined"
          color="primary"
          startIcon={<CloudUploadIcon />}
          onClick={() => fileInputRef.current?.click()}
          disabled={isBusy || isCaptureActive}
        >
          Upload Transcript
        </Button>
      </div>

      <div className={classes.recordingStatusRow}>
        <Typography variant="body2" className={classes.statusText}>
          {isCaptureActive ? (
            <>
              {isPaused ? "Paused" : "Recording"}{" "}
              <RecorderDuration
                registerElapsedTimeElement={registerElapsedTimeElement}
              />
            </>
          ) : isBusy ? (
            "Uploading and processing session..."
          ) : (
            "Ready to start a new session."
          )}
        </Typography>
        {!isBusy && (
          <div className={classes.audioMeterTrack}>
            <div ref={audioMeterFillRef} className={classes.audioMeterFill} />
          </div>
        )}
      </div>
      {isBusy && <LinearProgress />}
    </Paper>
  )
}

const RecorderDuration: FC<{
  registerElapsedTimeElement?: (element: HTMLElement) => () => void
}> = ({ registerElapsedTimeElement }) => {
  const durationElementRef = useRef<HTMLSpanElement | null>(null)

  useEffect(() => {
    const durationElement = durationElementRef.current
    return durationElement && registerElapsedTimeElement
      ? registerElapsedTimeElement(durationElement)
      : undefined
  }, [registerElapsedTimeElement])

  return <span ref={durationElementRef}>00:00</span>
}

const useStyles = makeStyles((theme) =>
  createStyles({
    capturePanel: {
      border: `1px solid ${colors.neutral.clay}`,
      borderRadius: 4,
      padding: theme.spacing(3),
      minHeight: "100%",
    },
    captureTitle: {
      fontWeight: 700,
      color: colors.neutral.denim,
      marginBottom: theme.spacing(1),
    },
    captureSubtitle: {
      color: colors.neutral.asphalt,
      marginBottom: theme.spacing(3),
    },
    actionsRow: {
      display: "flex",
      gap: theme.spacing(2),
      flexWrap: "wrap",
      marginBottom: theme.spacing(2),
    },
    stopButton: {
      backgroundColor: colors.secondary.crimson,
      color: colors.neutral.white,
      "&:hover": {
        backgroundColor: colors.secondary.crimson,
      },
    },
    hiddenInput: {
      display: "none",
    },
    recordingStatusRow: {
      display: "grid",
      gap: theme.spacing(1),
    },
    statusText: {
      color: colors.neutral.asphalt,
    },
    audioMeterTrack: {
      width: "100%",
      height: 8,
      borderRadius: 999,
      overflow: "hidden",
      backgroundColor: colors.neutral.clay,
    },
    audioMeterFill: {
      height: "100%",
      width: "0%",
      backgroundColor: colors.primary.periwinkle,
      transition: "width 100ms linear",
    },
  })
)
